// frontend/src/pages/admin/AdminDashboardPage.js

import React, { useEffect, useState } from 'react';
import { 
  Users, 
  DollarSign, 
  Package,
  RefreshCw,
  Filter,
  Download,
  ChevronRight,
  Home,
  CreditCard,
  Flag,
  Edit,
  Save,
  X,
  Smartphone,
  Zap,
  Shield,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Mail,
  Phone,
  Calendar,
  User,
  Store,
  Star,
  TrendingUp,
  BarChart3,
  Activity,
  Clock,
  Globe,
  Database,
  Wifi,
  Award,
  Megaphone,
  Search
} from 'lucide-react';
import StatsCard from '../../components/admin/StatsCard';
import UserTable from '../../components/admin/UserTable';
import TransactionTable from '../../components/admin/TransactionTable';
import WalletTransactionTable from '../../components/admin/WalletTransactionTable';
import DataProviderConfigTable from '../../components/admin/DataProviderConfigTable';
import useAdminStore from '../../store/adminStore';
import useAuthStore from '../../store/authStore';

const AdminDashboardPage = () => {
  const [timeRange, setTimeRange] = useState('30days');
  
  const [userFilters, setUserFilters] = useState({ page: 1, limit: 10, search: '' });
  const [transactionFilters, setTransactionFilters] = useState({ page: 1, limit: 10, search: '' });
  const [walletFilters, setWalletFilters] = useState({ page: 1, limit: 10, search: '' });
  const [vtuProductFilters, setVtuProductFilters] = useState({ page: 1, limit: 20, search: '' });
  
  const [editingProductId, setEditingProductId] = useState(null);
  const [editCommission, setEditCommission] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [resolutionAction, setResolutionAction] = useState('DISMISS_REPORT');
  const [resolutionText, setResolutionText] = useState('');
  
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);

  const {
    dashboardStats,
    users,
    usersPagination,
    transactions,
    transactionsPagination,
    walletTransactions,
    walletTransactionsPagination,
    flaggedContent,
    vtuProducts,
    dataProviderConfig,
    isLoading,
    activeTab,
    selectedUser,
    getDashboardStats,
    getUsers,
    getTransactions,
    getWalletTransactions,
    getFlaggedContent,
    getVtuProducts,
    getDataProviderConfig,
    updateVtuProductCommission,
    syncVtuProducts,
    resolveReport,
    removeContent,
    setActiveTab,
    setSelectedUser,
    setSelectedTransaction,
    setShowUserModal,
    setShowTransactionModal,
  } = useAdminStore();
  
  const { user } = useAuthStore();

  useEffect(() => {
    loadDashboard();
  }, [timeRange]);

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'transactions') {
      loadTransactions();
    } else if (activeTab === 'wallet') {
      loadWalletTransactions();
    } else if (activeTab === 'moderation') {
      loadFlaggedContent();
    } else if (activeTab === 'products') {
      loadVtuProducts();
    } else if (activeTab === 'providers') {
      getDataProviderConfig().catch(err =>
        console.error('Failed to load provider config:', err)
      );
    }
  }, [activeTab, userFilters, transactionFilters, walletFilters, vtuProductFilters]);

  const loadDashboard = async () => {
    try {
      await getDashboardStats({ range: parseInt(timeRange) });
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const { page, limit, search, ...rest } = userFilters;
      await getUsers({ ...rest, search, page: page || 1, limit: limit || 10 });
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadTransactions = async () => {
    try {
      const { page, limit, search, ...rest } = transactionFilters;
      await getTransactions({ ...rest, search, page: page || 1, limit: limit || 10 });
    } catch (error) {
      console.error('Failed to load transactions:', error);
    }
  };

  const loadWalletTransactions = async () => {
    try {
      const { page, limit, search, ...rest } = walletFilters;
      await getWalletTransactions({ ...rest, search, page: page || 1, limit: limit || 10 });
    } catch (error) {
      console.error('Failed to load wallet transactions:', error);
    }
  };

  const loadFlaggedContent = async () => {
    try {
      await getFlaggedContent({ limit: 10 });
    } catch (error) {
      console.error('Failed to load flagged content:', error);
    }
  };

  const loadVtuProducts = async () => {
    try {
      const { page, limit, search, ...rest } = vtuProductFilters;
      await getVtuProducts({ ...rest, search, page: page || 1, limit: limit || 20 });
    } catch (error) {
      console.error('Failed to load VTU products:', error);
    }
  };

  // Search Handlers
  const handleUserSearch = (searchTerm) => {
    setUserFilters({ ...userFilters, search: searchTerm, page: 1 });
    getUsers({ ...userFilters, search: searchTerm, page: 1, limit: userFilters.limit || 10 });
  };

  const handleTransactionSearch = (searchTerm) => {
    setTransactionFilters({ ...transactionFilters, search: searchTerm, page: 1 });
    getTransactions({ ...transactionFilters, search: searchTerm, page: 1, limit: transactionFilters.limit || 10 });
  };

  const handleWalletSearch = (searchTerm) => {
    setWalletFilters({ ...walletFilters, search: searchTerm, page: 1 });
    getWalletTransactions({ ...walletFilters, search: searchTerm, page: 1, limit: walletFilters.limit || 10 });
  };

  const handleSyncProducts = async () => {
    try {
      await syncVtuProducts();
      loadVtuProducts();
    } catch (error) {
      console.error('Failed to sync products:', error);
    }
  };

  const handleUpdateCommission = async (productId) => {
    if (!editCommission || isNaN(editCommission)) return;
    try {
      await updateVtuProductCommission(productId, parseFloat(editCommission));
      setEditingProductId(null);
      setEditCommission('');
    } catch (error) {
      console.error('Failed to update commission:', error);
    }
  };

  const handleResolveReport = async () => {
    if (!selectedReport) return;
    try {
      await resolveReport(selectedReport.id, resolutionAction, resolutionText);
      setShowReportModal(false);
      setSelectedReport(null);
      setResolutionText('');
      loadFlaggedContent();
    } catch (error) {
      console.error('Failed to resolve report:', error);
    }
  };

  const handleRemoveContent = async (contentType, contentId) => {
    try {
      await removeContent(contentType, contentId, 'Removed by admin during moderation');
      loadFlaggedContent();
    } catch (error) {
      console.error('Failed to remove content:', error);
    }
  };

  const handleViewUser = async (user) => {
    try {
      setSelectedUser(user);
      setShowUserDetailsModal(true);
    } catch (error) {
      console.error('Failed to load user details:', error);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUserForEdit(user);
    setShowEditUserModal(true);
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
  };

  const handleUserPageChange = (page, limit) => {
    setUserFilters({ ...userFilters, page, limit: limit || 10 });
    getUsers({ ...userFilters, page, limit: limit || 10 });
  };

  const handleTransactionPageChange = (page, limit) => {
    setTransactionFilters({ ...transactionFilters, page, limit: limit || 10 });
    getTransactions({ ...transactionFilters, page, limit: limit || 10 });
  };

  const handleWalletPageChange = (page, limit) => {
    setWalletFilters({ ...walletFilters, page, limit: limit || 10 });
    getWalletTransactions({ ...walletFilters, page, limit: limit || 10 });
  };

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: Home, color: 'primary' },
    { id: 'users', name: 'Users', icon: Users, color: 'blue' },
    { id: 'transactions', name: 'Service', icon: CreditCard, color: 'green' },
    { id: 'wallet', name: 'Wallet', icon: DollarSign, color: 'teal' },
    { id: 'moderation', name: 'Moderation', icon: Flag, color: 'yellow' },
    { id: 'products', name: 'Products', icon: Package, color: 'purple' },
    { id: 'providers', name: 'Providers', icon: Database, color: 'indigo' },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getContentTypeIcon = (type) => {
    switch(type) {
      case 'POST': return <Flag className="h-4 w-4 text-blue-600" />;
      case 'COMMENT': return <Star className="h-4 w-4 text-purple-600" />;
      case 'USER': return <User className="h-4 w-4 text-orange-600" />;
      default: return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      PENDING: { color: 'bg-yellow-50 text-yellow-700 border-yellow-200', label: 'Pending' },
      REVIEWED: { color: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Reviewed' },
      RESOLVED: { color: 'bg-green-50 text-green-700 border-green-200', label: 'Resolved' },
      DISMISSED: { color: 'bg-gray-50 text-gray-700 border-gray-200', label: 'Dismissed' },
    };
    const c = config[status] || config.PENDING;
    return <span className={`px-2 py-0.5 text-xs font-medium rounded border ${c.color}`}>{c.label}</span>;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                  <div>
                    <h3 className="text-base font-semibold text-gray-800">Overview</h3>
                    <p className="text-xs text-gray-500">Key metrics and insights</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value)}
                    className="px-2 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  >
                    <option value="7days">7 days</option>
                    <option value="30days">30 days</option>
                    <option value="90days">90 days</option>
                    <option value="365days">Year</option>
                  </select>
                  <button
                    onClick={loadDashboard}
                    disabled={isLoading}
                    className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center disabled:opacity-50 text-xs"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatsCard
                title="Total Users"
                value={dashboardStats?.users?.total || 0}
                icon="users"
                change={12}
                color="primary"
              />
              <StatsCard
                title="Total Revenue"
                value={dashboardStats?.revenue?.total || 0}
                icon="dollar"
                change={8}
                color="green"
                format="currency"
              />
              <StatsCard
                title="Success Rate"
                value={dashboardStats?.transactions?.successRate || 0}
                icon="check"
                change={3}
                color="blue"
                format="percent"
              />
              <StatsCard
                title="Pending Reports"
                value={dashboardStats?.community?.pendingReports || 0}
                icon="alert"
                change={-5}
                color="red"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatsCard
                title="Today's Revenue"
                value={dashboardStats?.revenue?.today || 0}
                icon="trending"
                color="green"
                format="currency"
              />
              <StatsCard
                title="Successful"
                value={dashboardStats?.transactions?.successful || 0}
                icon="check"
                color="blue"
              />
              <StatsCard
                title="Failed"
                value={dashboardStats?.transactions?.failed || 0}
                icon="x"
                color="red"
              />
              <StatsCard
                title="Total Posts"
                value={dashboardStats?.community?.totalPosts || 0}
                icon="message"
                color="purple"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl shadow-neomorph p-4 border border-primary-100/30">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                  <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                  Quick Actions
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setActiveTab('users')}
                    className="p-3 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all border border-transparent hover:border-gray-200 text-center"
                  >
                    <div className="p-1.5 bg-blue-50 rounded-lg mx-auto mb-1 w-fit">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                    <span className="text-xs font-medium text-gray-700">Users</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('transactions')}
                    className="p-3 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all border border-transparent hover:border-gray-200 text-center"
                  >
                    <div className="p-1.5 bg-green-50 rounded-lg mx-auto mb-1 w-fit">
                      <CreditCard className="h-5 w-5 text-green-600" />
                    </div>
                    <span className="text-xs font-medium text-gray-700">Transactions</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('wallet')}
                    className="p-3 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all border border-transparent hover:border-gray-200 text-center"
                  >
                    <div className="p-1.5 bg-teal-50 rounded-lg mx-auto mb-1 w-fit">
                      <DollarSign className="h-5 w-5 text-teal-600" />
                    </div>
                    <span className="text-xs font-medium text-gray-700">Wallet</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('moderation')}
                    className="p-3 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all border border-transparent hover:border-gray-200 text-center"
                  >
                    <div className="p-1.5 bg-yellow-50 rounded-lg mx-auto mb-1 w-fit">
                      <Flag className="h-5 w-5 text-yellow-600" />
                    </div>
                    <span className="text-xs font-medium text-gray-700">Moderation</span>
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-neomorph p-4 border border-primary-100/30">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                  <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                  Recent Activity
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all cursor-pointer">
                    <div>
                      <p className="text-xs font-medium text-gray-800">New user registered</p>
                      <p className="text-[10px] text-gray-500">2 min ago</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all cursor-pointer">
                    <div>
                      <p className="text-xs font-medium text-gray-800">Transaction failed</p>
                      <p className="text-[10px] text-gray-500">15 min ago</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all cursor-pointer">
                    <div>
                      <p className="text-xs font-medium text-gray-800">Content reported</p>
                      <p className="text-[10px] text-gray-500">1 hour ago</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'users':
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full mr-2" />
                  <div>
                    <h3 className="text-base font-semibold text-gray-800">User Management</h3>
                    <p className="text-xs text-gray-500">Manage all user accounts</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={userFilters.role || ''}
                    onChange={(e) => setUserFilters({ ...userFilters, role: e.target.value || undefined })}
                    className="px-2 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  >
                    <option value="">All Roles</option>
                    <option value="USER">Users</option>
                    <option value="RESELLER">Resellers</option>
                    <option value="ADMIN">Admins</option>
                  </select>
                  <button className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center text-xs">
                    <Filter className="h-3 w-3 mr-1.5" />
                    Filters
                  </button>
                  <button className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center text-xs">
                    <Download className="h-3 w-3 mr-1.5" />
                    Export
                  </button>
                </div>
              </div>
            </div>

            <UserTable
              users={users}
              pagination={usersPagination}
              onView={handleViewUser}
              onEdit={handleEditUser}
              onRefresh={loadUsers}
              onPageChange={handleUserPageChange}
              onSearch={handleUserSearch}
              searchTerm={userFilters.search}
              isLoading={isLoading}
            />

            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-500">
                Showing {users?.length || 0} of {usersPagination?.total || 0} users
              </p>
              <button
                onClick={() => setActiveTab('users')}
                className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center text-xs"
              >
                View All
                <ChevronRight className="h-3 w-3 ml-1" />
              </button>
            </div>
          </div>
        );

      case 'transactions':
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-green-400 to-green-600 rounded-full mr-2" />
                  <div>
                    <h3 className="text-base font-semibold text-gray-800">Service Transactions</h3>
                    <p className="text-xs text-gray-500">Monitor all service transactions</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={transactionFilters.status || ''}
                    onChange={(e) => setTransactionFilters({ ...transactionFilters, status: e.target.value || undefined })}
                    className="px-2 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  >
                    <option value="">All Status</option>
                    <option value="SUCCESS">Successful</option>
                    <option value="PENDING">Pending</option>
                    <option value="FAILED">Failed</option>
                    <option value="REVERSED">Reversed</option>
                  </select>
                  <button className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center text-xs">
                    <Filter className="h-3 w-3 mr-1.5" />
                    Filters
                  </button>
                  <button className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center text-xs">
                    <Download className="h-3 w-3 mr-1.5" />
                    Export
                  </button>
                </div>
              </div>
            </div>

            <TransactionTable
              transactions={transactions}
              pagination={transactionsPagination}
              onView={handleViewTransaction}
              onRefresh={loadTransactions}
              onPageChange={handleTransactionPageChange}
              onSearch={handleTransactionSearch}
              searchTerm={transactionFilters.search}
              isLoading={isLoading}
            />

            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-500">
                Showing {transactions?.length || 0} of {transactionsPagination?.total || 0}
              </p>
              <button
                onClick={() => setActiveTab('transactions')}
                className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center text-xs"
              >
                View All
                <ChevronRight className="h-3 w-3 ml-1" />
              </button>
            </div>
          </div>
        );

      case 'wallet':
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-teal-400 to-teal-600 rounded-full mr-2" />
                  <div>
                    <h3 className="text-base font-semibold text-gray-800">Wallet Transactions</h3>
                    <p className="text-xs text-gray-500">Funding and refund transactions</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={walletFilters.type || ''}
                    onChange={(e) => setWalletFilters({ ...walletFilters, type: e.target.value || undefined })}
                    className="px-2 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  >
                    <option value="">All Types</option>
                    <option value="CREDIT">Credits</option>
                    <option value="DEBIT">Debits</option>
                  </select>
                  <select
                    value={walletFilters.status || ''}
                    onChange={(e) => setWalletFilters({ ...walletFilters, status: e.target.value || undefined })}
                    className="px-2 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  >
                    <option value="">All Status</option>
                    <option value="SUCCESS">Successful</option>
                    <option value="PENDING">Pending</option>
                    <option value="FAILED">Failed</option>
                    <option value="REVERSED">Reversed</option>
                  </select>
                  <button
                    onClick={loadWalletTransactions}
                    disabled={isLoading}
                    className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center disabled:opacity-50 text-xs"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            <WalletTransactionTable
              transactions={walletTransactions}
              pagination={walletTransactionsPagination}
              onRefresh={loadWalletTransactions}
              onPageChange={handleWalletPageChange}
              onSearch={handleWalletSearch}
              searchTerm={walletFilters.search}
              isLoading={isLoading}
            />

            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-500">
                Showing {walletTransactions?.length || 0} of {walletTransactionsPagination?.total || 0}
              </p>
              <button
                onClick={() => window.location.href = '/admin/transactions/wallet'}
                className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center text-xs"
              >
                View All
                <ChevronRight className="h-3 w-3 ml-1" />
              </button>
            </div>
          </div>
        );

      case 'moderation':
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-full mr-2" />
                  <div>
                    <h3 className="text-base font-semibold text-gray-800">Content Moderation</h3>
                    <p className="text-xs text-gray-500">Review reported content</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={userFilters.contentType || ''}
                    onChange={(e) => setUserFilters({ ...userFilters, contentType: e.target.value || undefined })}
                    className="px-2 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  >
                    <option value="">All Types</option>
                    <option value="POST">Posts</option>
                    <option value="COMMENT">Comments</option>
                    <option value="USER">Users</option>
                  </select>
                  <button className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center text-xs">
                    <Filter className="h-3 w-3 mr-1.5" />
                    Filters
                  </button>
                  <button
                    onClick={loadFlaggedContent}
                    disabled={isLoading}
                    className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center disabled:opacity-50 text-xs"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {flaggedContent.length > 0 ? (
              <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-primary-100/50 text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Content</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Reporter</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-primary-100/50">
                      {flaggedContent.map((report) => {
                        const contentType = report.postId ? 'POST' : report.commentId ? 'COMMENT' : 'USER';
                        const content = report.post?.content || report.comment?.content || report.reportedUser?.email || 'N/A';
                        
                        let reportedUserName = 'N/A';
                        if (report.reportedUser) {
                          reportedUserName = `${report.reportedUser.firstName || ''} ${report.reportedUser.lastName || ''}`.trim();
                        } else if (report.post?.user) {
                          reportedUserName = `${report.post.user.firstName || ''} ${report.post.user.lastName || ''}`.trim();
                        } else if (report.comment?.user) {
                          reportedUserName = `${report.comment.user.firstName || ''} ${report.comment.user.lastName || ''}`.trim();
                        }
                        
                        return (
                          <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="p-1 rounded bg-blue-50 mr-1.5">
                                  {getContentTypeIcon(contentType)}
                                </div>
                                <span className="text-gray-800">{contentType}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="font-medium text-gray-800">{reportedUserName}</div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="text-gray-600 max-w-xs truncate">{content}</div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="text-gray-800">{report.reporter?.firstName} {report.reporter?.lastName}</div>
                              <div className="text-[10px] text-gray-500">{report.reporter?.email}</div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="text-gray-600">{report.reason}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {getStatusBadge(report.status)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                              {new Date(report.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <button
                                onClick={() => {
                                  setSelectedReport(report);
                                  setShowReportModal(true);
                                }}
                                className="p-1.5 bg-primary-50 rounded-lg hover:bg-primary-100 transition-all"
                                title="View report"
                              >
                                <Eye className="h-3.5 w-3.5 text-primary-600" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-neomorph p-6 text-center border border-primary-100/30">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-yellow-50 shadow-neomorph-inset mb-3">
                  <Flag className="h-7 w-7 text-yellow-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-800 mb-1">No Pending Reports</h3>
                <p className="text-sm text-gray-500">All reports have been reviewed and resolved.</p>
              </div>
            )}
          </div>
        );

      case 'products':
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-purple-400 to-purple-600 rounded-full mr-2" />
                  <div>
                    <h3 className="text-base font-semibold text-gray-800">VTU Products</h3>
                    <p className="text-xs text-gray-500">Manage products and commissions</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 flex-wrap gap-2">
                  {/* Search input for products */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={vtuProductFilters.search || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setVtuProductFilters({ ...vtuProductFilters, search: value, page: 1 });
                        getVtuProducts({ ...vtuProductFilters, search: value, page: 1, limit: vtuProductFilters.limit || 20 });
                      }}
                      className="pl-8 pr-3 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs w-40 md:w-60"
                    />
                    {vtuProductFilters.search && (
                      <button
                        onClick={() => {
                          setVtuProductFilters({ ...vtuProductFilters, search: '', page: 1 });
                          getVtuProducts({ ...vtuProductFilters, search: '', page: 1, limit: vtuProductFilters.limit || 20 });
                        }}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <select
                    value={vtuProductFilters.network || ''}
                    onChange={(e) => setVtuProductFilters({ ...vtuProductFilters, network: e.target.value || undefined })}
                    className="px-2 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  >
                    <option value="">All Networks</option>
                    <option value="MTN">MTN</option>
                    <option value="AIRTEL">Airtel</option>
                    <option value="GLO">Glo</option>
                    <option value="ETISALAT">9mobile</option>
                  </select>
                  <select
                    value={vtuProductFilters.type || ''}
                    onChange={(e) => setVtuProductFilters({ ...vtuProductFilters, type: e.target.value || undefined })}
                    className="px-2 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  >
                    <option value="">All Types</option>
                    <option value="AIRTIME">Airtime</option>
                    <option value="DATA">Data</option>
                  </select>
                  <button
                    onClick={handleSyncProducts}
                    disabled={isLoading}
                    className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center disabled:opacity-50 text-xs"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                    Sync
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-primary-100/50 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Network</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-primary-100/50">
                    {vtuProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="p-1 rounded bg-blue-50 mr-1.5">
                              <Smartphone className="h-3.5 w-3.5 text-blue-600" />
                            </div>
                            <span className="font-medium text-gray-800">{product.network}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-gray-800">{product.name}</div>
                          <div className="text-[10px] text-gray-500">{product.code}</div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-700 rounded border border-purple-200">
                            {product.type}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-800">
                          {formatCurrency(product.amount)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {editingProductId === product.id ? (
                            <div className="flex items-center space-x-1.5">
                              <input
                                type="number"
                                value={editCommission}
                                onChange={(e) => setEditCommission(e.target.value)}
                                className="w-16 px-1.5 py-1 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                                min="0"
                                max="100"
                                step="0.1"
                              />
                              <button
                                onClick={() => handleUpdateCommission(product.id)}
                                className="p-1 bg-green-50 rounded hover:bg-green-100"
                                title="Save"
                              >
                                <Save className="h-3.5 w-3.5 text-green-600" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingProductId(null);
                                  setEditCommission('');
                                }}
                                className="p-1 bg-red-50 rounded hover:bg-red-100"
                                title="Cancel"
                              >
                                <X className="h-3.5 w-3.5 text-red-600" />
                              </button>
                            </div>
                          ) : (
                            <span className="font-medium text-gray-800">{product.commission}%</span>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${
                            product.isActive 
                              ? 'bg-green-50 text-green-700 border-green-200' 
                              : 'bg-gray-50 text-gray-700 border-gray-200'
                          }`}>
                            {product.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <button
                            onClick={() => {
                              setEditingProductId(product.id);
                              setEditCommission(product.commission.toString());
                            }}
                            className="p-1.5 bg-primary-50 rounded-lg hover:bg-primary-100 transition-all"
                            title="Edit commission"
                          >
                            <Edit className="h-3.5 w-3.5 text-primary-600" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'providers':
        return <DataProviderConfigTable />;

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#E8ECF5] rounded-xl p-3 shadow-neomorph border border-primary-100/50">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center">
            <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
            <div>
              <h1 className="text-lg font-bold text-gray-800">Admin Dashboard</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                Welcome back, <span className="font-semibold text-primary-600">{user?.firstName} {user?.lastName}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-700 border border-red-200">
              <Shield className="h-3 w-3 mr-1" />
              Admin
            </span>
            <button 
              onClick={() => {
                Promise.all([
                  loadDashboard(),
                  loadUsers(),
                  loadTransactions(),
                  loadWalletTransactions(),
                  loadFlaggedContent(),
                  loadVtuProducts(),
                ]);
              }}
              className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center text-xs"
            >
              <RefreshCw className="h-3 w-3 mr-1.5" />
              Refresh All
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const colorClasses = {
                  primary: 'from-primary-500 to-primary-600',
                  blue: 'from-blue-500 to-blue-600',
                  green: 'from-green-500 to-green-600',
                  teal: 'from-teal-500 to-teal-600',
                  yellow: 'from-yellow-500 to-yellow-600',
                  purple: 'from-purple-500 to-purple-600',
                  indigo: 'from-indigo-500 to-indigo-600',
                };
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      w-full flex items-center px-3 py-2 text-xs font-medium rounded-lg transition-all border
                      ${activeTab === tab.id
                        ? `bg-gradient-to-r ${colorClasses[tab.color]} text-white shadow-md border-transparent`
                        : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover border-transparent'
                      }
                    `}
                  >
                    <div className={`p-1 rounded-lg mr-2 ${
                      activeTab === tab.id 
                        ? 'bg-white/20' 
                        : `bg-${tab.color}-50`
                    }`}>
                      <tab.icon className={`h-3.5 w-3.5 ${
                        activeTab === tab.id ? 'text-white' : `text-${tab.color}-600`
                      }`} />
                    </div>
                    <span className="flex-1 text-left">{tab.name}</span>
                    {(tab.id === 'moderation' && dashboardStats?.community?.pendingReports > 0) && (
                      <span className="ml-auto bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                        {dashboardStats.community.pendingReports}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            <div className="mt-4 pt-4 border-t border-primary-100/50">
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2">Management</p>
              <div className="space-y-1">
                <button
                  onClick={() => window.location.href = '/admin/announcements'}
                  className="w-full flex items-center px-3 py-2 text-xs font-medium rounded-lg transition-all text-gray-600 hover:bg-white hover:shadow-neomorph-hover border border-transparent hover:border-gray-100"
                >
                  <div className="p-1 rounded-lg mr-2 bg-pink-50">
                    <Megaphone className="h-3.5 w-3.5 text-pink-600" />
                  </div>
                  <span className="flex-1 text-left">Announcements</span>
                </button>
                <button
                  onClick={() => window.location.href = '/admin/promotions'}
                  className="w-full flex items-center px-3 py-2 text-xs font-medium rounded-lg transition-all text-gray-600 hover:bg-white hover:shadow-neomorph-hover border border-transparent hover:border-gray-100"
                >
                  <div className="p-1 rounded-lg mr-2 bg-indigo-50">
                    <Award className="h-3.5 w-3.5 text-indigo-600" />
                  </div>
                  <span className="flex-1 text-left">Promotions</span>
                </button>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-primary-100/50">
              <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center">
                <div className="w-1 h-3 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5" />
                System Status
              </h4>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between p-1.5 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="p-0.5 bg-green-100 rounded mr-1.5">
                      <Globe className="h-2.5 w-2.5 text-green-600" />
                    </div>
                    <span className="text-[10px] text-gray-600">API</span>
                  </div>
                  <span className="inline-flex items-center text-[10px] text-green-600">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-500 mr-1"></div>
                    Operational
                  </span>
                </div>
                <div className="flex items-center justify-between p-1.5 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="p-0.5 bg-green-100 rounded mr-1.5">
                      <Database className="h-2.5 w-2.5 text-green-600" />
                    </div>
                    <span className="text-[10px] text-gray-600">Database</span>
                  </div>
                  <span className="inline-flex items-center text-[10px] text-green-600">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-500 mr-1"></div>
                    Connected
                  </span>
                </div>
                <div className="flex items-center justify-between p-1.5 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="p-0.5 bg-green-100 rounded mr-1.5">
                      <Wifi className="h-2.5 w-2.5 text-green-600" />
                    </div>
                    <span className="text-[10px] text-gray-600">VTU</span>
                  </div>
                  <span className="inline-flex items-center text-[10px] text-green-600">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-500 mr-1"></div>
                    Online
                  </span>
                </div>
                <div className="flex items-center justify-between p-1.5 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="p-0.5 bg-green-100 rounded mr-1.5">
                      <CreditCard className="h-2.5 w-2.5 text-green-600" />
                    </div>
                    <span className="text-[10px] text-gray-600">Paystack</span>
                  </div>
                  <span className="inline-flex items-center text-[10px] text-green-600">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-500 mr-1"></div>
                    Connected
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          {renderTabContent()}
        </div>
      </div>

      {/* Report Resolution Modal */}
      {showReportModal && selectedReport && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-600/50 backdrop-blur-sm" onClick={() => setShowReportModal(false)}></div>

            <div className="inline-block align-bottom bg-[#E8ECF5] rounded-xl shadow-neomorph px-4 pt-4 pb-4 text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-5 border border-primary-100/30">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-xl bg-yellow-50 shadow-neomorph-inset sm:mx-0 sm:h-8 sm:w-8">
                  <Flag className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="mt-2 text-center sm:mt-0 sm:ml-3 sm:text-left w-full">
                  <h3 className="text-base font-semibold text-gray-800">Resolve Report</h3>
                  <div className="mt-1.5">
                    <p className="text-xs text-gray-500">
                      From {selectedReport.reporter?.firstName} {selectedReport.reporter?.lastName}
                    </p>
                    <p className="text-xs font-medium text-gray-700 mt-1">Reason: {selectedReport.reason}</p>
                    {selectedReport.description && (
                      <p className="text-xs text-gray-600 mt-0.5">{selectedReport.description}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Action</label>
                <select
                  value={resolutionAction}
                  onChange={(e) => setResolutionAction(e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                >
                  <option value="DISMISS_REPORT">Dismiss Report</option>
                  <option value="WARN_USER">Warn User</option>
                  <option value="REMOVE_CONTENT">Remove Content</option>
                  <option value="SUSPEND_USER">Suspend User</option>
                  <option value="BAN_USER">Ban User</option>
                </select>

                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Resolution Notes</label>
                  <textarea
                    value={resolutionText}
                    onChange={(e) => setResolutionText(e.target.value)}
                    rows="2"
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    placeholder="Add resolution notes..."
                  />
                </div>
              </div>

              <div className="mt-4 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleResolveReport}
                  disabled={isLoading || !resolutionText.trim()}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent px-3 py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-xs font-medium text-white shadow-md shadow-primary-500/30 hover:shadow-lg focus:outline-none sm:ml-3 sm:w-auto disabled:opacity-50"
                >
                  {isLoading ? 'Processing...' : 'Resolve'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowReportModal(false);
                    setSelectedReport(null);
                    setResolutionText('');
                  }}
                  className="mt-2 w-full inline-flex justify-center rounded-lg border border-gray-200 px-3 py-1.5 bg-white text-xs font-medium text-gray-700 shadow-neomorph hover:shadow-neomorph-hover focus:outline-none sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserDetailsModal && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-600/50 backdrop-blur-sm" onClick={() => setShowUserDetailsModal(false)}></div>

            <div className="inline-block align-bottom bg-[#E8ECF5] rounded-xl shadow-neomorph px-4 pt-4 pb-4 text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-5 border border-primary-100/30">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-base font-semibold text-gray-800">User Details</h3>
                <button
                  onClick={() => setShowUserDetailsModal(false)}
                  className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover"
                >
                  <X className="h-3.5 w-3.5 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-md flex items-center justify-center">
                    {selectedUser.avatar ? (
                      <img src={selectedUser.avatar} alt={selectedUser.firstName} className="h-12 w-12 rounded-xl object-cover" />
                    ) : (
                      <span className="text-white text-lg font-bold">
                        {selectedUser.firstName?.[0]}{selectedUser.lastName?.[0]}
                      </span>
                    )}
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-gray-800">{selectedUser.firstName} {selectedUser.lastName}</h4>
                    <p className="text-xs text-gray-500">{selectedUser.email}</p>
                    <p className="text-xs text-gray-500">{selectedUser.phone}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="p-2 bg-white rounded-lg shadow-neomorph-inset">
                    <p className="text-[10px] text-gray-500">Role</p>
                    <p className="text-xs font-semibold text-gray-800">{selectedUser.role}</p>
                  </div>
                  <div className="p-2 bg-white rounded-lg shadow-neomorph-inset">
                    <p className="text-[10px] text-gray-500">Status</p>
                    <p className="text-xs font-semibold text-gray-800">
                      {selectedUser.isBanned ? 'Banned' : selectedUser.isSuspended ? 'Suspended' : 'Active'}
                    </p>
                  </div>
                  <div className="p-2 bg-white rounded-lg shadow-neomorph-inset">
                    <p className="text-[10px] text-gray-500">Balance</p>
                    <p className="text-xs font-semibold text-gray-800">{formatCurrency(selectedUser.wallet?.balance || 0)}</p>
                  </div>
                  <div className="p-2 bg-white rounded-lg shadow-neomorph-inset">
                    <p className="text-[10px] text-gray-500">Phone Verified</p>
                    <p className="text-xs font-semibold text-gray-800">{selectedUser.phoneVerified ? 'Yes' : 'No'}</p>
                  </div>
                  <div className="p-2 bg-white rounded-lg shadow-neomorph-inset">
                    <p className="text-[10px] text-gray-500">Joined</p>
                    <p className="text-xs font-semibold text-gray-800">{new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div className="p-2 bg-white rounded-lg shadow-neomorph-inset">
                    <p className="text-[10px] text-gray-500">Last Login</p>
                    <p className="text-xs font-semibold text-gray-800">
                      {selectedUser.lastLogin ? new Date(selectedUser.lastLogin).toLocaleDateString() : 'Never'}
                    </p>
                  </div>
                </div>

                {selectedUser.businessTags && selectedUser.businessTags.length > 0 && (
                  <div className="p-2 bg-white rounded-lg shadow-neomorph-inset">
                    <p className="text-[10px] text-gray-500 mb-1">Business Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedUser.businessTags.map((tag, index) => (
                        <span key={index} className="px-1.5 py-0.5 bg-primary-50 text-primary-700 text-[10px] rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedUser.bio && (
                  <div className="p-2 bg-white rounded-lg shadow-neomorph-inset">
                    <p className="text-[10px] text-gray-500 mb-0.5">Bio</p>
                    <p className="text-xs text-gray-700">{selectedUser.bio}</p>
                  </div>
                )}
              </div>

              <div className="mt-3 flex justify-end">
                <button
                  onClick={() => setShowUserDetailsModal(false)}
                  className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover text-xs"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && selectedUserForEdit && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-600/50 backdrop-blur-sm" onClick={() => setShowEditUserModal(false)}></div>

            <div className="inline-block align-bottom bg-[#E8ECF5] rounded-xl shadow-neomorph px-4 pt-4 pb-4 text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-5 border border-primary-100/30">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-base font-semibold text-gray-800">Edit User</h3>
                <button
                  onClick={() => setShowEditUserModal(false)}
                  className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover"
                >
                  <X className="h-3.5 w-3.5 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">First Name</label>
                  <input
                    type="text"
                    defaultValue={selectedUserForEdit.firstName}
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Last Name</label>
                  <input
                    type="text"
                    defaultValue={selectedUserForEdit.lastName}
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Email</label>
                  <input
                    type="email"
                    defaultValue={selectedUserForEdit.email}
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Phone</label>
                  <input
                    type="tel"
                    defaultValue={selectedUserForEdit.phone}
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Role</label>
                  <select
                    defaultValue={selectedUserForEdit.role}
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  >
                    <option value="USER">User</option>
                    <option value="RESELLER">Reseller</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Status</label>
                  <select
                    defaultValue={selectedUserForEdit.isBanned ? 'BANNED' : selectedUserForEdit.isSuspended ? 'SUSPENDED' : 'ACTIVE'}
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="SUSPENDED">Suspended</option>
                    <option value="BANNED">Banned</option>
                  </select>
                </div>
              </div>
              
              <div className="mt-4 flex justify-end space-x-2">
                <button
                  onClick={() => setShowEditUserModal(false)}
                  className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover text-xs"
                >
                  Cancel
                </button>
                <button
                  className="px-3 py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg text-xs"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;